import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="table-filter"
export default class extends Controller {
  static targets = ["freqMin", "freqMax", "connectorCheckbox", "table", "info", "cardsGrid"];
  static values = {
    connectorColumn: Number,
    debounceDelay: { type: Number, default: 500 },
  };

  connect() {
    this.debounceTimeout = null;
    this.isFilteringActive = false;

    // Get table ID for debugging
    const viewToggleElement = this.element.querySelector("[data-view-toggle-table-id-value]") || this.element;
    this.tableId = viewToggleElement.dataset.viewToggleTableIdValue || "unknown";

    // Initialize filter settings FIRST (separate from UI state)
    this.initializeFilterSettings();

    // Always initialize filters (cards should always be available)
    this.initializeFilters();

    // Populate filter inputs from saved settings and apply filters (after settings are initialized)
    // Use longer delay to ensure browser restoration is complete
    setTimeout(() => {
      this.reapplyFiltersWithTableCheck();
    }, 200);

    // Store event handlers for cleanup
    this.handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from cache, reapply filters
        setTimeout(() => {
          this.reapplyFiltersWithTableCheck();
        }, 100);
      }
    };

    this.handleTurboLoad = () => {
      setTimeout(() => {
        this.reapplyFiltersWithTableCheck();
      }, 100);
    };

    // Listen for browser navigation events (back/forward)
    window.addEventListener('pageshow', this.handlePageShow);

    // Also listen for Turbo navigation events
    document.addEventListener('turbo:load', this.handleTurboLoad);

    // Listen for view switches to reset CSS and reapply filters
    this.element.addEventListener("view-toggle:switched", (event) => {
      this.resetCSSAndReapplyFilters();
    });

    // Also listen for table being loaded dynamically via Turbo Frames
    this.handleFrameLoad = (event) => {
      // Check if the frame load is for a table within this controller's element
      if (this.element.contains(event.target)) {
        setTimeout(() => {
          this.initializeFilters();
          this.resetCSSAndReapplyFilters();
        }, 100);
      }
    };
    document.addEventListener("turbo:frame-load", this.handleFrameLoad);

    this.bindEvents();
  }

  initializeFilterSettings() {
    const storageKey = `filter_settings_${this.tableId}`;
    
    // Check if filters should be cleared based on navigation
    if (this.shouldClearFilters()) {
      localStorage.removeItem(storageKey);
      this.filterSettings = this.getDefaultFilterSettings();
      return;
    }
    
    // Load saved filters
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const data = JSON.parse(stored);
        this.filterSettings = data.settings;
      } catch (e) {
        this.filterSettings = this.getDefaultFilterSettings();
      }
    } else {
      this.filterSettings = this.getDefaultFilterSettings();
    }
  }

  shouldClearFilters() {
    // Store current page info to detect navigation between product pages
    const currentPath = window.location.pathname;
    const lastPath = sessionStorage.getItem('lastProductPage');
    
    // Update the stored path
    sessionStorage.setItem('lastProductPage', currentPath);
    
    // Clear filters if:
    // 1. Coming from a different product listing page
    // 2. First visit (no last path)
    if (lastPath && lastPath !== currentPath && 
        lastPath.includes('/products/') && currentPath.includes('/products/')) {
      return true;
    }
    
    // Also clear if this is the first visit to any product page
    if (!lastPath && currentPath.includes('/products/')) {
      return true;
    }
    
    return false;
  }

  getDefaultFilterSettings() {
    return {
      freqMin: null,
      freqMax: null,
      connectors: [],
    };
  }

  saveFilterSettings() {
    const storageKey = `filter_settings_${this.tableId}`;
    const data = {
      settings: this.filterSettings,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  }

  resetCSSAndReapplyFilters() {
    // Step 1: Clear any classes that have to do with hiding or filtering
    this.clearAllFilterClasses();

    // Step 2: Rebuild connector filters to ensure fresh DOM bindings
    this.buildConnectorFilters();

    // Step 3: Populate the filter bar inputs with whatever is in the filter settings variable
    this.populateFilterInputs();

    // Step 4: Run the filter code to properly hide the proper cards/table rows
    this.applyFilters(false); // Don't preserve scroll during reset/initialization
  }

  populateFilterInputs() {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // Update frequency inputs from settings (convert from GHz storage to display unit)
    if (this.hasFreqMinTarget) {
      if (this.filterSettings.freqMin !== null) {
        const displayValue =
          this.frequencyUnit === "MHz" ? this.filterSettings.freqMin * 1000 : this.filterSettings.freqMin;
        this.freqMinTarget.value = displayValue;
      } else {
        this.freqMinTarget.value = "";
      }
    }
    if (this.hasFreqMaxTarget) {
      if (this.filterSettings.freqMax !== null) {
        const displayValue =
          this.frequencyUnit === "MHz" ? this.filterSettings.freqMax * 1000 : this.filterSettings.freqMax;
        this.freqMaxTarget.value = displayValue;
      } else {
        this.freqMaxTarget.value = "";
      }
    }

    // Update connector checkboxes from settings
    if (this.hasConnectorCheckboxTargets) {
      this.connectorCheckboxTargets.forEach((checkbox) => {
        checkbox.checked = this.filterSettings.connectors.includes(checkbox.value);
      });
    }
  }

  initializeFilters() {
    // Discover table-specific settings if table exists
    if (this.hasTableTarget) {
      this.discoverFrequencyColumns();
      this.discoverFrequencyUnit();
      this.discoverConnectorColumn();
    } else {
      this.discoverFrequencyUnit();
    }

    // Always try to build connector filters (works with both table and cards)
    this.buildConnectorFilters();
  }

  disconnect() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    // Clean up event listeners
    if (this.handlePageShow) {
      window.removeEventListener('pageshow', this.handlePageShow);
    }
    if (this.handleTurboLoad) {
      document.removeEventListener('turbo:load', this.handleTurboLoad);
    }
    
    // Remove any event listeners from this element
    this.element.removeEventListener('view-toggle:switched', this.resetCSSAndReapplyFilters);
    
    // Remove frame load listener
    if (this.handleFrameLoad) {
      document.removeEventListener('turbo:frame-load', this.handleFrameLoad);
    }
    
    // Clear filter state to free memory
    this.filterSettings = null;
    
    // Remove any DataTable search extensions that might have been added
    // Note: This is tricky because extensions are global, but we'll clear them
    // in the main application.js turbo:before-render handler
  }

  discoverFrequencyColumns() {
    if (!this.hasTableTarget) return;

    // Find frequency columns by looking for a colspan header with "frequency"
    // that has two sub-headers (Min/Max)
    const headerRows = this.tableTarget.querySelectorAll("thead tr");
    this.freqMinColumn = 0; // default
    this.freqMaxColumn = 1; // default

    if (headerRows.length >= 2) {
      // First row typically has colspan headers
      const firstRowCells = headerRows[0].querySelectorAll("th");
      let currentColumn = 0;

      for (let i = 0; i < firstRowCells.length; i++) {
        const cell = firstRowCells[i];
        const colspan = parseInt(cell.getAttribute("colspan")) || 1;
        const headerText = cell.textContent.toLowerCase();

        // Look for frequency-related header with colspan=2
        if (headerText.includes("frequency") && colspan === 2 && !headerText.includes("center")) {
          this.freqMinColumn = currentColumn;
          this.freqMaxColumn = currentColumn + 1;
          break;
        }

        // Always increment by colspan (rowspan doesn't affect column counting)
        currentColumn += colspan;
      }
    }
  }

  discoverFrequencyUnit() {
    this.frequencyUnit = "GHz"; // default
    this.frequencyMultiplier = 1; // multiplier to convert to GHz for internal calculations
    this.hasMixedUnits = false; // flag for tables with units in cells

    // Check table headers if table exists
    if (this.hasTableTarget) {
      const headerCells = this.tableTarget.querySelectorAll("thead th");

      // Look for frequency-related headers - check all headers
      for (let i = 0; i < headerCells.length; i++) {
        const headerText = headerCells[i].textContent.toLowerCase();
        if (headerText.includes("frequency")) {
          if (headerText.includes("mhz")) {
            this.frequencyUnit = "MHz";
            this.frequencyMultiplier = 1000; // MHz to GHz conversion
            break;
          } else if (headerText.includes("ghz")) {
            this.frequencyUnit = "GHz";
            this.frequencyMultiplier = 1;
            break;
          } else {
            // No unit in header, check if units are in cells
            this.checkForMixedUnits();
          }
        }
      }
    }

    // If no table or no unit found in table, check cards
    if (!this.hasTableTarget && this.hasCardsGridTarget) {
      this.discoverFrequencyUnitFromCards();
    } else if (this.hasTableTarget && this.hasCardsGridTarget && this.frequencyUnit === "GHz") {
      // Check cards as fallback if table didn't give us a clear unit
      this.discoverFrequencyUnitFromCards();
    }

    // Update the filter label
    const filterLabel = this.element.querySelector(".frequency-filter label");
    if (filterLabel) {
      filterLabel.textContent = `Frequency (${this.frequencyUnit}):`;
    }

    // Update input step based on unit
    const stepValue = this.frequencyUnit === "MHz" ? "1" : "0.001";
    if (this.freqMinTarget) {
      this.freqMinTarget.setAttribute("step", stepValue);
    }
    if (this.freqMaxTarget) {
      this.freqMaxTarget.setAttribute("step", stepValue);
    }
  }

  checkForMixedUnits() {
    // Check first few rows to see if units are specified in cells
    const rows = this.tableTarget.querySelectorAll("tbody tr");
    for (let i = 0; i < Math.min(3, rows.length); i++) {
      const cells = rows[i].querySelectorAll("td");
      if (cells.length >= 2) {
        const cellText = cells[0].textContent.toLowerCase() + cells[1].textContent.toLowerCase();
        if (cellText.includes("mhz") || cellText.includes("ghz") || cellText.includes("khz")) {
          this.hasMixedUnits = true;
          // Keep filter in GHz for mixed units
          this.frequencyUnit = "GHz";
          this.frequencyMultiplier = 1;
          break;
        }
      }
    }
  }

  discoverFrequencyUnitFromCards() {
    if (!this.hasCardsGridTarget) return;

    // Check frequency values in cards to determine predominant unit
    const cards = this.cardsGridTarget.querySelectorAll(".product-card");
    let mhzCount = 0;
    let ghzCount = 0;
    const foundFreqTexts = [];

    // Sample first few cards to determine unit
    const sampleSize = Math.min(5, cards.length);
    for (let i = 0; i < sampleSize; i++) {
      const card = cards[i];

      // Check card header frequency
      const productFreq = card.querySelector(".product-freq");
      if (productFreq) {
        const freqText = productFreq.textContent.toLowerCase();
        foundFreqTexts.push(freqText);
        if (freqText.includes("mhz")) {
          mhzCount++;
        } else if (freqText.includes("ghz")) {
          ghzCount++;
        }
      }

      // Also check spec items for frequency
      const specs = card.querySelectorAll(".card-spec-item");
      specs.forEach((spec) => {
        const label = spec.querySelector(".card-spec-label");
        if (label && label.textContent.toLowerCase().includes("frequency")) {
          const value = spec.querySelector(".card-spec-value");
          if (value) {
            const freqText = value.textContent.toLowerCase();
            foundFreqTexts.push(freqText);
            if (freqText.includes("mhz")) {
              mhzCount++;
            } else if (freqText.includes("ghz")) {
              ghzCount++;
            }
          }
        }
      });
    }

    // Set unit based on what we found most of
    if (mhzCount > ghzCount && mhzCount > 0) {
      this.frequencyUnit = "MHz";
      this.frequencyMultiplier = 1000;
    } else if (ghzCount > 0) {
      this.frequencyUnit = "GHz";
      this.frequencyMultiplier = 1;
    }
    // If we found mixed units or no clear winner, mark as mixed
    if (mhzCount > 0 && ghzCount > 0) {
      this.hasMixedUnits = true;
      this.frequencyUnit = "GHz"; // Use GHz as base for mixed units
      this.frequencyMultiplier = 1;
    }
  }

  discoverConnectorColumn() {
    if (!this.hasTableTarget) return;

    let connectorColumn = -1;
    let columnType = "Connector"; // default

    // Find the connector/package column by scanning headers for "package" or "connector" text
    const headerRows = this.tableTarget.querySelectorAll("thead tr");

    // For multi-row headers, we need to build a proper column mapping
    // Start by analyzing the first row which defines the main column structure
    if (headerRows.length > 0) {
      const firstRow = headerRows[0];
      const headers = firstRow.querySelectorAll("th");
      let currentColumn = 0;

      headers.forEach((header) => {
        const colspan = parseInt(header.getAttribute("colspan")) || 1;
        const rowspan = parseInt(header.getAttribute("rowspan")) || 1;
        const headerText = header.textContent.trim().toLowerCase();

        // Check if this header contains "package" or "connector" - treat as interchangeable
        // Normalize text by removing line breaks and extra spaces for better matching
        const normalizedText = headerText.replace(/\s+/g, " ").trim();
        if (normalizedText.includes("package") || normalizedText.includes("connector")) {
          connectorColumn = currentColumn;
          columnType = normalizedText.includes("package") ? "Package" : "Connector";
        }

        currentColumn += colspan;
      });
    }

    // If not found in first row, check subsequent rows (but be more careful about column counting)
    if (connectorColumn === -1 && headerRows.length > 1) {
      // For subsequent rows, we need to account for rowspan headers from previous rows
      // This is more complex, but let's try a simpler approach first
      for (let rowIndex = 1; rowIndex < headerRows.length; rowIndex++) {
        const row = headerRows[rowIndex];
        const headers = row.querySelectorAll("th");

        headers.forEach((header, headerIndex) => {
          const headerText = header.textContent.trim().toLowerCase();

          if (headerText.includes("package") || headerText.includes("connector")) {
            // For multi-row headers, we'll need to calculate the actual column position
            // This is a simplified approach - we could make it more sophisticated if needed
            if (connectorColumn === -1) {
              connectorColumn = headerIndex;
              columnType = headerText.includes("package") ? "Package" : "Connector";
            }
          }
        });
      }
    }

    this.connectorColumnValue = connectorColumn;
    this.connectorColumnType = columnType;
  }

  buildConnectorFilters() {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    const connectorValues = new Set();

    // Get connector values from THIS instance's table (if it exists)
    if (this.hasTableTarget && this.connectorColumnValue >= 0) {
      const rows = this.tableTarget.querySelectorAll("tbody tr");
      rows.forEach((row, index) => {
        const cells = row.querySelectorAll("td");
        if (cells[this.connectorColumnValue]) {
          const cellText = cells[this.connectorColumnValue].textContent.trim();
          if (cellText) {
            connectorValues.add(cellText);
          }
        }
      });
    }

    // Also get connector values from THIS instance's cards (if they exist)
    if (this.hasCardsGridTarget) {
      const cards = this.cardsGridTarget.querySelectorAll(".product-card");
      cards.forEach((card) => {
        const connectorSpecs = card.querySelectorAll(".card-spec-item");
        connectorSpecs.forEach((spec) => {
          const label = spec.querySelector(".card-spec-label");
          const labelText = label ? label.textContent.toLowerCase() : "";
          // Treat "connector type" and "package type" as interchangeable
          if (label && (labelText.includes("connector") || labelText.includes("package"))) {
            const value = spec.querySelector(".card-spec-value");
            if (value) {
              const connectorText = value.textContent.trim();
              if (connectorText) {
                connectorValues.add(connectorText);
              }
            }
          }
        });
      });
    }

    // If no connectors found from either source, return early
    if (connectorValues.size === 0) return;

    const connectorFilter = this.element.querySelector(".connector-filter");

    // If there's only one unique value, hide the entire connector filter
    if (connectorValues.size <= 1) {
      connectorFilter.style.display = "none";
      return;
    }

    // Show the filter if it was previously hidden
    connectorFilter.style.display = "";

    // Update the filter label based on detected column type
    const filterLabel = connectorFilter.querySelector("label:first-child");
    if (filterLabel) {
      // Determine the label based on what we found
      let labelText = this.connectorColumnType || "Connector"; // fallback to "Connector"

      // If we only have cards and found package-type labels, use "Package"
      if (!this.hasTableTarget && this.hasCardsGridTarget) {
        // Check if any cards have "package" in their labels
        const cards = this.cardsGridTarget.querySelectorAll(".product-card");
        let hasPackageType = false;
        cards.forEach((card) => {
          const specs = card.querySelectorAll(".card-spec-item");
          specs.forEach((spec) => {
            const label = spec.querySelector(".card-spec-label");
            if (label && label.textContent.toLowerCase().includes("package")) {
              hasPackageType = true;
            }
          });
        });
        if (hasPackageType) {
          labelText = "Package";
        }
      }

      filterLabel.textContent = labelText + ":";
    }

    // Create checkboxes for each unique connector value found
    const container = connectorFilter.querySelector(".connector-checkboxes");
    container.innerHTML = ""; // Clear existing checkboxes

    Array.from(connectorValues)
      .sort()
      .forEach((connectorValue) => {
        const label = document.createElement("label");
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.value = connectorValue;
        checkbox.setAttribute("data-table-filter-target", "connectorCheckbox");
        checkbox.setAttribute("data-action", "change->table-filter#filterConnectors");

        // Restore checked state based on current filter settings
        checkbox.checked = this.filterSettings && this.filterSettings.connectors.includes(connectorValue);

        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(" " + connectorValue));
        container.appendChild(label);
      });
  }

  reapplyFiltersWithTableCheck() {
    // Check if we're in table view and if table needs to be loaded
    const tableViewContainer = this.element.querySelector('[data-view-toggle-target="tableView"]');
    const cardsViewContainer = this.element.querySelector('[data-view-toggle-target="cardsView"]');
    
    const tableVisible = this.hasTableTarget && tableViewContainer && !tableViewContainer.classList.contains("hidden");
    const cardsVisible = this.hasCardsGridTarget && cardsViewContainer && !cardsViewContainer.classList.contains("hidden");
    
    if (tableVisible) {
      // We're in table view, check if table is loaded
      const tableContainer = this.element.querySelector('[data-view-toggle-target="tableContainer"]');
      const hasTable = tableContainer && tableContainer.querySelector('.productTable');
      
      if (!hasTable) {
        // Table not loaded yet, wait for it to load via Turbo Frame and then apply filters
        const handleTableLoaded = (event) => {
          if (this.element.contains(event.target)) {
            document.removeEventListener("turbo:frame-load", handleTableLoaded);
            setTimeout(() => {
              this.resetCSSAndReapplyFilters();
            }, 50);
          }
        };
        document.addEventListener("turbo:frame-load", handleTableLoaded);
        
        // Trigger table loading if needed
        const viewToggleController = this.application.getControllerForElementAndIdentifier(
          this.element.closest('[data-controller*="view-toggle"]') || this.element,
          'view-toggle'
        );
        if (viewToggleController && typeof viewToggleController.showTable === 'function') {
          viewToggleController.showTable();
        }
      } else {
        // Table is already loaded, apply filters normally
        this.resetCSSAndReapplyFilters();
      }
    } else {
      // Cards view or unknown state, apply filters normally
      this.resetCSSAndReapplyFilters();
    }
  }

  bindEvents() {
    // Events are handled via data-action attributes in HTML
    // This method can be removed or used for additional setup
  }

  handleFrequencyInput() {
    // Clear existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Set new timeout to delay filtering
    this.debounceTimeout = setTimeout(() => {
      this.filterFrequency();
    }, this.debounceDelayValue);
  }

  handleFrequencyChange() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.filterFrequency();
  }

  filterFrequency() {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // Get values in the unit displayed to user
    const minVal = parseFloat(this.freqMinTarget.value) || null;
    const maxVal = parseFloat(this.freqMaxTarget.value) || null;

    // Update filter settings (always stored in GHz for consistency)
    if (this.frequencyUnit === "MHz") {
      this.filterSettings.freqMin = minVal !== null ? minVal / 1000 : null;
      this.filterSettings.freqMax = maxVal !== null ? maxVal / 1000 : null;
    } else {
      this.filterSettings.freqMin = minVal;
      this.filterSettings.freqMax = maxVal;
    }

    // Save to localStorage
    this.saveFilterSettings();

    // Preserve scroll position during user filtering
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;
    
    this.applyFilters();
    
    // Restore scroll position after filtering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(scrollLeft, scrollTop);
      });
    });
  }

  filterConnectors() {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // Update filter settings from checkbox state
    this.filterSettings.connectors = [];

    this.connectorCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        this.filterSettings.connectors.push(checkbox.value);
      }
    });

    // Save to localStorage
    this.saveFilterSettings();

    // Preserve scroll position during user filtering
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;
    
    this.applyFilters();
    
    // Restore scroll position after filtering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(scrollLeft, scrollTop);
      });
    });
  }

  clearAllFilterClasses() {
    // Clear filter-hidden class from all table rows
    if (this.hasTableTarget) {
      const tableRows = this.tableTarget.querySelectorAll("tbody tr");
      tableRows.forEach((row) => {
        row.classList.remove("filter-hidden");
        row.style.display = ""; // Also clear inline styles
      });
    }

    // Clear filter-hidden class from all cards
    if (this.hasCardsGridTarget) {
      const cards = this.cardsGridTarget.querySelectorAll(".product-card");
      cards.forEach((card) => {
        card.classList.remove("filter-hidden");
        card.style.display = ""; // Also clear inline styles
      });
    }
  }

  resetFilters() {
    // Save current scroll position before resetting
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;

    // Reset filter settings to defaults
    this.filterSettings = this.getDefaultFilterSettings();

    // Save to localStorage
    this.saveFilterSettings();

    // Manually do the reset steps but preserve scroll
    // Step 1: Clear any classes that have to do with hiding or filtering
    this.clearAllFilterClasses();
    
    // Step 2: Rebuild connector filters to ensure fresh DOM bindings
    this.buildConnectorFilters();
    
    // Step 3: Populate the filter bar inputs with whatever is in the filter settings variable
    this.populateFilterInputs();

    // Step 4: Run the filter code but PRESERVE scroll this time
    this.applyFilters(true).then(() => {
      // Extra scroll restoration just to be sure
      requestAnimationFrame(() => {
        window.scrollTo(scrollLeft, scrollTop);
      });
    });
  }

  async applyFilters(preserveScroll = true) {
    // Prevent concurrent filtering operations
    if (this.isFilteringActive) {
      return;
    }

    this.isFilteringActive = true;

    // Save current scroll position only if preserveScroll is true
    let scrollTop, scrollLeft;
    if (preserveScroll) {
      scrollTop = window.scrollY;
      scrollLeft = window.scrollX;
    }

    // Try to use View Transitions API for smoother updates (modern browsers)
    const supportsViewTransitions = "startViewTransition" in document;

    const applyChanges = () => {
      // Use more aggressive performance optimizations
      const performanceMode = this.hasTableTarget
        ? {
            originalContain: this.tableTarget.style.contain,
            originalTransform: this.tableTarget.style.transform,
            originalWillChange: this.tableTarget.style.willChange,
          }
        : null;

      // Force GPU acceleration and containment (only if table exists)
      if (this.hasTableTarget) {
        this.tableTarget.style.contain = "strict";
        this.tableTarget.style.transform = "translateZ(0)";
        this.tableTarget.style.willChange = "transform, opacity";
        this.tableTarget.classList.add("filtering-active");

        // Add view transition class if supported
        if (supportsViewTransitions) {
          this.tableTarget.classList.add("view-transition-active");
          // Set unique view transition name per table
          this.tableTarget.style.viewTransitionName = `table-filter-${this.tableId}`;
        }
      }

      let visibleCount = 0;
      let totalCount = 0;
      const changes = [];

      // Determine which view is currently active by checking visibility
      // Check the parent view containers, not the inner elements
      const tableViewContainer = this.element.querySelector('[data-view-toggle-target="tableView"]');
      const cardsViewContainer = this.element.querySelector('[data-view-toggle-target="cardsView"]');

      const tableVisible =
        this.hasTableTarget && tableViewContainer && !tableViewContainer.classList.contains("hidden");
      const cardsVisible =
        this.hasCardsGridTarget && cardsViewContainer && !cardsViewContainer.classList.contains("hidden");

      // Since only one view can be visible at a time, prioritize cards if visible
      if (cardsVisible) {
        // Filter cards ONLY within this controller's element
        const cards = this.cardsGridTarget.querySelectorAll(".product-card");

        cards.forEach((card) => {
          totalCount++;
          const shouldShow = this.shouldShowElement(card, "card");
          const isCurrentlyHidden = card.classList.contains("filter-hidden");

          if (shouldShow) {
            visibleCount++;
            if (isCurrentlyHidden) {
              changes.push({ row: card, action: "show" });
            }
          } else {
            if (!isCurrentlyHidden) {
              changes.push({ row: card, action: "hide" });
            }
          }
        });
      } else if (tableVisible) {
        // Filter table rows ONLY within this controller's element
        const rows = this.tableTarget.querySelectorAll("tbody tr");

        rows.forEach((row, index) => {
          totalCount++;
          const shouldShow = this.shouldShowElement(row, "table");
          const isCurrentlyHidden = row.classList.contains("filter-hidden");

          if (shouldShow) {
            visibleCount++;
            if (isCurrentlyHidden) {
              changes.push({ row, action: "show" });
            }
          } else {
            if (!isCurrentlyHidden) {
              changes.push({ row, action: "hide" });
            }
          }
        });
      }

      // Apply all changes using the most efficient method available
      if (changes.length > 0) {
        // Use a temporary document fragment to minimize DOM manipulation
        const fragment = document.createDocumentFragment();

        // Batch class changes to minimize style recalculation
        const hideElements = [];
        const showElements = [];

        changes.forEach(({ row, action }) => {
          if (action === "hide") {
            hideElements.push(row);
          } else {
            showElements.push(row);
          }
        });

        // Batch DOM updates to reduce layout thrashing
        document.documentElement.style.setProperty("--filtering-transition", "0.1s");

        // Apply all hide operations first
        hideElements.forEach((row) => {
          row.classList.add("filter-hidden");
          row.style.display = "none";
        });
        // Then all show operations
        showElements.forEach((row) => {
          row.classList.remove("filter-hidden");
          row.style.display = "";
        });

        // Reset transition after updates
        setTimeout(() => {
          document.documentElement.style.removeProperty("--filtering-transition");
        }, 150);
      }

      // Check for data compatibility issues and provide helpful info
      if (this.filterSettings && this.filterSettings.connectors.length > 0 && visibleCount === 0 && totalCount > 0) {
        // Show a more helpful message when data structure mismatch occurs
        this.updateFilterInfo(0, totalCount, "No items match the selected connector filter in this view");
      } else {
        // Update filter info immediately
        this.updateFilterInfo(visibleCount, totalCount);
      }

      // Restripe visible rows
      this.restripeRows();

      // Cleanup and scroll restoration
      requestAnimationFrame(() => {
        // Restore performance settings (only if table exists)
        if (this.hasTableTarget && performanceMode) {
          this.tableTarget.style.contain = performanceMode.originalContain;
          this.tableTarget.style.transform = performanceMode.originalTransform;
          this.tableTarget.style.willChange = performanceMode.originalWillChange;
          this.tableTarget.classList.remove("filtering-active");

          if (supportsViewTransitions) {
            this.tableTarget.classList.remove("view-transition-active");
            this.tableTarget.style.viewTransitionName = "";
          }
        }

        // Use a second requestAnimationFrame to ensure scroll restoration happens after layout
        requestAnimationFrame(() => {
          // Restore scroll position after layout is complete (only if we preserved it)
          if (preserveScroll) {
            window.scrollTo(scrollLeft, scrollTop);
          }

          // Mark filtering as complete
          this.isFilteringActive = false;
        });
      });
    };

    // Use View Transitions API if available for smoother updates
    if (supportsViewTransitions) {
      try {
        await document.startViewTransition(applyChanges).finished;
      } catch (error) {
        // Fallback to regular method if view transition fails
        applyChanges();
      }
    } else {
      applyChanges();
    }
  }

  // Unified filtering logic - determines if an element should be shown
  shouldShowElement(element, type) {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // Parse data from the element
    const data = this.parseElementData(element, type);

    // Apply frequency filter
    if (this.filterSettings.freqMin !== null || this.filterSettings.freqMax !== null) {
      if (!this.passesFrequencyFilter(data.freqMinGHz, data.freqMaxGHz)) {
        return false;
      }
    }

    // Apply connector filter
    if (this.filterSettings.connectors.length > 0) {
      if (!this.passesConnectorFilter(data.connector)) {
        return false;
      }
    }
    return true;
  }

  // Parse data from an element (table row or card)
  parseElementData(element, type) {
    if (type === "table") {
      return this.parseTableRowData(element);
    } else {
      return this.parseCardData(element);
    }
  }

  // Unified frequency filtering logic
  passesFrequencyFilter(freqMinGHz, freqMaxGHz) {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // Min filter: product's min frequency should be <= filter min value
    if (this.filterSettings.freqMin !== null && freqMinGHz > this.filterSettings.freqMin) {
      return false;
    }

    // Max filter: product's max frequency should be >= filter max value
    if (this.filterSettings.freqMax !== null && freqMaxGHz < this.filterSettings.freqMax) {
      return false;
    }

    // Min filter: product's max frequency must be >= filter min (product range must reach filter min)
    if (this.filterSettings.freqMin !== null && freqMaxGHz < this.filterSettings.freqMin) {
      return false;
    }

    // Max filter: product's min frequency must be <= filter max (product range must start before filter max)
    if (this.filterSettings.freqMax !== null && freqMinGHz > this.filterSettings.freqMax) {
      return false;
    }

    return true;
  }

  // Unified connector filtering logic
  passesConnectorFilter(connectorValue) {
    // Safety check - ensure filterSettings is initialized
    if (!this.filterSettings) {
      this.filterSettings = this.getDefaultFilterSettings();
    }

    // If no connector value available, gracefully pass filter
    // This handles cases where cards and tables have different connector data structures
    if (!connectorValue || connectorValue.trim() === "") {
      return false; // Don't show items without connector data when filtering by connector
    }

    return this.filterSettings.connectors.includes(connectorValue);
  }

  // Parse data from table row
  parseTableRowData(row) {
    const cells = row.querySelectorAll("td");
    let freqMinGHz = 0;
    let freqMaxGHz = 0;
    let connector = "";

    // Parse frequency data
    if (cells.length > Math.max(this.freqMinColumn, this.freqMaxColumn)) {
      const freqMinCell = cells[this.freqMinColumn].textContent.trim();
      const freqMaxCell = cells[this.freqMaxColumn].textContent.trim();

      // Parse frequency min value with unit detection
      if (freqMinCell) {
        if (freqMinCell.toLowerCase() === "dc") {
          freqMinGHz = 0;
        } else {
          const freqMinText = freqMinCell.toLowerCase().trim();
          const freqMinVal = parseFloat(freqMinText) || 0;

          if (freqMinText.includes("khz")) {
            freqMinGHz = freqMinVal / 1000000; // Convert kHz to GHz
          } else if (freqMinText.includes("mhz")) {
            freqMinGHz = freqMinVal / 1000; // Convert MHz to GHz
          } else if (freqMinText.includes("ghz")) {
            freqMinGHz = freqMinVal; // Already in GHz
          } else {
            // No unit specified, use detected default unit
            if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
              freqMinGHz = freqMinVal / 1000;
            } else {
              freqMinGHz = freqMinVal; // Assume GHz
            }
          }
        }
      }

      // Parse frequency max value with unit detection
      if (freqMaxCell) {
        const freqMaxText = freqMaxCell.toLowerCase().trim();
        const freqMaxVal = parseFloat(freqMaxText) || 0;

        if (freqMaxText.includes("khz")) {
          freqMaxGHz = freqMaxVal / 1000000; // Convert kHz to GHz
        } else if (freqMaxText.includes("mhz")) {
          freqMaxGHz = freqMaxVal / 1000; // Convert MHz to GHz
        } else if (freqMaxText.includes("ghz")) {
          freqMaxGHz = freqMaxVal; // Already in GHz
        } else {
          // No unit specified, use detected default unit
          if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
            freqMaxGHz = freqMaxVal / 1000;
          } else {
            freqMaxGHz = freqMaxVal; // Assume GHz
          }
        }
      }
    }

    // Parse connector data
    if (this.connectorColumnValue >= 0 && this.connectorColumnValue < cells.length) {
      connector = cells[this.connectorColumnValue].textContent.trim();
    }

    return { freqMinGHz, freqMaxGHz, connector };
  }

  // Parse data from card
  parseCardData(card) {
    let freqMinGHz = 0;
    let freqMaxGHz = 0;
    let connector = "";
    let foundFrequency = false;
    let freqMinText = "";
    let freqMaxText = "";

    // Parse frequency from card header first
    const productFreq = card.querySelector(".product-freq");
    if (productFreq) {
      const freqText = productFreq.textContent.trim();
      const freqMatch = freqText.match(/(DC|\d+\.?\d*)\s*-\s*(\d+\.?\d*)/i);
      if (freqMatch) {
        foundFrequency = true;
        freqMinText = freqMatch[1];
        freqMaxText = freqMatch[2] + " " + (freqText.toLowerCase().includes("mhz") ? "MHz" : "GHz");
      }
    }

    // If not found in header, try the specs section
    if (!foundFrequency) {
      const frequencySpecs = card.querySelectorAll(".card-spec-item");
      frequencySpecs.forEach((spec) => {
        const label = spec.querySelector(".card-spec-label");
        if (label && label.textContent.toLowerCase().includes("frequency")) {
          foundFrequency = true;
          const value = spec.querySelector(".card-spec-value");
          if (value) {
            const freqText = value.textContent.trim();
            const freqMatch = freqText.match(/(DC|\d+\.?\d*)\s*-\s*(\d+\.?\d*)/i);
            if (freqMatch) {
              freqMinText = freqMatch[1];
              freqMaxText = freqMatch[2] + " " + (freqText.toLowerCase().includes("mhz") ? "MHz" : "GHz");
            }
          }
        }
      });
    }

    // Parse frequency min value with unit detection (same logic as table)
    if (freqMinText) {
      if (freqMinText.toLowerCase() === "dc") {
        freqMinGHz = 0;
      } else {
        const freqMinTextLower = freqMinText.toLowerCase().trim();
        const freqMinVal = parseFloat(freqMinTextLower) || 0;

        if (freqMinTextLower.includes("khz")) {
          freqMinGHz = freqMinVal / 1000000; // Convert kHz to GHz
        } else if (freqMinTextLower.includes("mhz")) {
          freqMinGHz = freqMinVal / 1000; // Convert MHz to GHz
        } else if (freqMinTextLower.includes("ghz")) {
          freqMinGHz = freqMinVal; // Already in GHz
        } else {
          // No unit specified, use detected default unit
          if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
            freqMinGHz = freqMinVal / 1000;
          } else {
            freqMinGHz = freqMinVal; // Assume GHz
          }
        }
      }
    }

    // Parse frequency max value with unit detection (same logic as table)
    if (freqMaxText) {
      const freqMaxTextLower = freqMaxText.toLowerCase().trim();
      const freqMaxVal = parseFloat(freqMaxTextLower) || 0;

      if (freqMaxTextLower.includes("khz")) {
        freqMaxGHz = freqMaxVal / 1000000; // Convert kHz to GHz
      } else if (freqMaxTextLower.includes("mhz")) {
        freqMaxGHz = freqMaxVal / 1000; // Convert MHz to GHz
      } else if (freqMaxTextLower.includes("ghz")) {
        freqMaxGHz = freqMaxVal; // Already in GHz
      } else {
        // No unit specified, use detected default unit
        if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
          freqMaxGHz = freqMaxVal / 1000;
        } else {
          freqMaxGHz = freqMaxVal; // Assume GHz
        }
      }
    }

    // Parse connector data
    const connectorSpecs = card.querySelectorAll(".card-spec-item");
    connectorSpecs.forEach((spec) => {
      const label = spec.querySelector(".card-spec-label");
      const labelText = label ? label.textContent.toLowerCase() : "";
      if (label && (labelText.includes("connector") || labelText.includes("package"))) {
        const value = spec.querySelector(".card-spec-value");
        if (value) {
          connector = value.textContent.trim();
        }
      }
    });

    return { freqMinGHz, freqMaxGHz, connector };
  }

  checkRowFrequency(row) {
    const cells = row.querySelectorAll("td");
    if (cells.length <= Math.max(this.freqMinColumn, this.freqMaxColumn)) return true;

    const freqMinCell = cells[this.freqMinColumn].textContent.trim();
    const freqMaxCell = cells[this.freqMaxColumn].textContent.trim();

    let freqMinGHz = 0;
    let freqMaxGHz = 0;

    // Parse frequency min value with unit detection
    if (freqMinCell) {
      if (freqMinCell.toLowerCase() === "dc") {
        freqMinGHz = 0;
      } else {
        const freqMinText = freqMinCell.toLowerCase().trim();
        // Extract numeric value - handle spaces between number and unit
        const freqMinVal = parseFloat(freqMinText) || 0;

        if (freqMinText.includes("khz")) {
          freqMinGHz = freqMinVal / 1000000; // Convert kHz to GHz
        } else if (freqMinText.includes("mhz")) {
          freqMinGHz = freqMinVal / 1000; // Convert MHz to GHz
        } else if (freqMinText.includes("ghz")) {
          freqMinGHz = freqMinVal; // Already in GHz
        } else {
          // No unit specified, use detected default unit
          if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
            freqMinGHz = freqMinVal / 1000;
          } else {
            freqMinGHz = freqMinVal; // Assume GHz
          }
        }
      }
    }

    // Parse frequency max value with unit detection
    if (freqMaxCell) {
      const freqMaxText = freqMaxCell.toLowerCase().trim();
      // Extract numeric value - handle spaces between number and unit
      const freqMaxVal = parseFloat(freqMaxText) || 0;

      if (freqMaxText.includes("khz")) {
        freqMaxGHz = freqMaxVal / 1000000; // Convert kHz to GHz
      } else if (freqMaxText.includes("mhz")) {
        freqMaxGHz = freqMaxVal / 1000; // Convert MHz to GHz
      } else if (freqMaxText.includes("ghz")) {
        freqMaxGHz = freqMaxVal; // Already in GHz
      } else {
        // No unit specified, use detected default unit
        if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
          freqMaxGHz = freqMaxVal / 1000;
        } else {
          freqMaxGHz = freqMaxVal; // Assume GHz
        }
      }
    }

    // Original filter logic (keep existing behavior):
    // Min filter: product's min frequency should be <= filter min value
    if (this.filterSettings.freqMin !== null && freqMinGHz > this.filterSettings.freqMin) {
      return false;
    }

    // Max filter: product's max frequency should be >= filter max value
    if (this.filterSettings.freqMax !== null && freqMaxGHz < this.filterSettings.freqMax) {
      return false;
    }

    // NEW: Additional overlap check to fix the range issue
    // Min filter: product's max frequency must be >= filter min (product range must reach filter min)
    if (this.filterSettings.freqMin !== null && freqMaxGHz < this.filterSettings.freqMin) {
      return false;
    }

    // Max filter: product's min frequency must be <= filter max (product range must start before filter max)
    if (this.filterSettings.freqMax !== null && freqMinGHz > this.filterSettings.freqMax) {
      return false;
    }

    return true;
  }

  checkRowConnector(row) {
    const cells = row.querySelectorAll("td");
    if (this.connectorColumnValue >= cells.length) return true;

    const connectorVal = cells[this.connectorColumnValue].textContent.trim();
    return this.filterSettings.connectors.includes(connectorVal);
  }

  checkCardFrequency(card) {
    let freqMinGHz = 0;
    let freqMaxGHz = 0;
    let foundFrequency = false;
    let freqMinText = "";
    let freqMaxText = "";

    // First, try to find frequency in the card header (more reliable)
    // Note: card header is nested inside the card-header-link
    const productFreq = card.querySelector(".product-freq");
    if (productFreq) {
      const freqText = productFreq.textContent.trim();
      // Parse frequency range like "2.0 - 18.0 GHz" or "DC-18 GHz"
      const freqMatch = freqText.match(/(DC|\d+\.?\d*)\s*-\s*(\d+\.?\d*)/i);
      if (freqMatch) {
        foundFrequency = true;
        freqMinText = freqMatch[1];
        freqMaxText = freqMatch[2] + " " + (freqText.toLowerCase().includes("mhz") ? "MHz" : "GHz");
      }
    }

    // If not found in header, try the specs section
    if (!foundFrequency) {
      const frequencySpecs = card.querySelectorAll(".card-spec-item");
      frequencySpecs.forEach((spec) => {
        const label = spec.querySelector(".card-spec-label");
        if (label && label.textContent.toLowerCase().includes("frequency")) {
          foundFrequency = true;
          const value = spec.querySelector(".card-spec-value");
          if (value) {
            const freqText = value.textContent.trim();
            // Parse frequency range like "2.0 - 18.0 GHz" or "DC-18 GHz"
            const freqMatch = freqText.match(/(DC|\d+\.?\d*)\s*-\s*(\d+\.?\d*)/i);
            if (freqMatch) {
              freqMinText = freqMatch[1];
              freqMaxText = freqMatch[2] + " " + (freqText.toLowerCase().includes("mhz") ? "MHz" : "GHz");
            }
          }
        }
      });
    }

    if (!foundFrequency) {
      return true;
    }

    // Parse frequency min value with unit detection (same logic as table)
    if (freqMinText) {
      if (freqMinText.toLowerCase() === "dc") {
        freqMinGHz = 0;
      } else {
        const freqMinTextLower = freqMinText.toLowerCase().trim();
        // Extract numeric value - handle spaces between number and unit
        const freqMinVal = parseFloat(freqMinTextLower) || 0;

        if (freqMinTextLower.includes("khz")) {
          freqMinGHz = freqMinVal / 1000000; // Convert kHz to GHz
        } else if (freqMinTextLower.includes("mhz")) {
          freqMinGHz = freqMinVal / 1000; // Convert MHz to GHz
        } else if (freqMinTextLower.includes("ghz")) {
          freqMinGHz = freqMinVal; // Already in GHz
        } else {
          // No unit specified, use detected default unit
          if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
            freqMinGHz = freqMinVal / 1000;
          } else {
            freqMinGHz = freqMinVal; // Assume GHz
          }
        }
      }
    }

    // Parse frequency max value with unit detection (same logic as table)
    if (freqMaxText) {
      const freqMaxTextLower = freqMaxText.toLowerCase().trim();
      // Extract numeric value - handle spaces between number and unit
      const freqMaxVal = parseFloat(freqMaxTextLower) || 0;

      if (freqMaxTextLower.includes("khz")) {
        freqMaxGHz = freqMaxVal / 1000000; // Convert kHz to GHz
      } else if (freqMaxTextLower.includes("mhz")) {
        freqMaxGHz = freqMaxVal / 1000; // Convert MHz to GHz
      } else if (freqMaxTextLower.includes("ghz")) {
        freqMaxGHz = freqMaxVal; // Already in GHz
      } else {
        // No unit specified, use detected default unit
        if (this.frequencyUnit === "MHz" && !this.hasMixedUnits) {
          freqMaxGHz = freqMaxVal / 1000;
        } else {
          freqMaxGHz = freqMaxVal; // Assume GHz
        }
      }
    }

    // Filter logic (same as table):
    // Min filter: show products where product's min frequency <= filter value
    // Max filter: show products where product's max frequency >= filter value
    // Both filters: apply both conditions

    if (this.filterSettings.freqMin !== null && freqMinGHz > this.filterSettings.freqMin) {
      return false;
    }

    if (this.filterSettings.freqMax !== null && freqMaxGHz < this.filterSettings.freqMax) {
      return false;
    }

    return true;
  }

  checkCardConnector(card) {
    const connectorSpecs = card.querySelectorAll(".card-spec-item");
    let connectorValue = "";

    connectorSpecs.forEach((spec) => {
      const label = spec.querySelector(".card-spec-label");
      const labelText = label ? label.textContent.toLowerCase() : "";
      if (label && (labelText.includes("connector") || labelText.includes("package"))) {
        const value = spec.querySelector(".card-spec-value");
        if (value) {
          connectorValue = value.textContent.trim();
        }
      }
    });

    return this.filterSettings.connectors.includes(connectorValue);
  }

  updateFilterInfo(visibleCount, totalCount, customMessage = null) {
    if (this.hasInfoTarget) {
      if (customMessage) {
        this.infoTarget.textContent = customMessage;
      } else if (visibleCount === totalCount) {
        this.infoTarget.textContent = "";
      } else {
        this.infoTarget.textContent = `Showing ${visibleCount} matches (filtered from ${totalCount} total)`;
      }
    }
  }

  restripeRows() {
    if (!this.hasTableTarget) return;

    // Use requestAnimationFrame to avoid blocking the main thread
    requestAnimationFrame(() => {
      const rows = this.tableTarget.querySelectorAll("tbody tr");
      let visibleRowIndex = 0;

      // Batch class operations
      const operations = [];

      rows.forEach((row) => {
        // Remove any manual striping classes
        operations.push(() => row.classList.remove("manual-odd", "manual-even"));

        // Only stripe visible rows
        if (!row.classList.contains("filter-hidden")) {
          if (visibleRowIndex % 2 === 0) {
            operations.push(() => row.classList.add("manual-odd"));
          } else {
            operations.push(() => row.classList.add("manual-even"));
          }
          visibleRowIndex++;
        }
      });

      // Execute all operations in a single batch
      operations.forEach((op) => op());
    });
  }

}
